// The ORCA API (api.theorca.pro) for the Phase 6A pages: the account and its
// shared settings, the portfolio, purchase history, market quotes and news.
//
// Responses are validated with Zod and turned into the UI types the pages
// already use. Business rules stay on the server: plan limits, interval menus,
// the schedule, P/L and which news the plan opens all come from the API; this
// file only renames fields. The session is the HttpOnly cookie; writes send the
// in-memory CSRF token.
import { z } from 'zod';
import i18n, { type Lang } from '@/i18n';
import { COINS } from '@/lib/coins';
import { requestJson, HttpError } from './http';
import { getCsrf } from './session';
import { nf, pct, price as fmtPrice } from '@/lib/format';
import { money } from '@/features/alerts/format';
import type { SocialXPage, SocialYtPage, Account, Alerts, AssetDay, Calendar, CalEvent, Delivery, Digest, HistoryCheck, Idea, NewsItem, NewsPage, Notice, PlanOffer, Portfolio,
  PriceEstimate, Profile, PurchaseLot, Signals, Social, TimelineEvent, WallStreet, WhaleTx } from '@/types';

type Out<T extends z.ZodTypeAny> = z.infer<T>;

/* ---------- API schemas (snake_case, as the server sends them) ---------- */
const PlanKey = z.enum(['free', 'essential', 'plus', 'premium']);
const ApiAccount = z.object({
  account_id: z.number(),
  plan: PlanKey.nullable(),
  plan_status: z.string(),
  active_until: z.string().nullable(),
  trial: z.object({ active: z.boolean(), used: z.boolean(), ends_at: z.string().nullable() }),
  free_status: z.string().nullable(),
  language: z.enum(['ru', 'en', 'de']),
}).passthrough();
const ApiEntitlements = z.object({
  plan: PlanKey.nullable(),
  limits: z.object({ coins: z.number(), ai_daily: z.number() }),
  capabilities: z.record(z.object({ available: z.boolean(), plan: z.enum(['free', 'plus', 'premium']) })),
  plan_limits: z.record(z.object({ coins: z.number(), ai_daily: z.number() })),
  trial_days: z.number(),
}).passthrough();
const ApiNewsSetting = z.object({
  available: z.literal(true),
  enabled: z.boolean().nullable(),
  interval_hours: z.number().nullable(),
  interval_options: z.array(z.number()),
  per_delivery: z.number(),
  schedule: z.object({ anchor_at: z.string().nullable(), anchor_source: z.string().nullable(), next_run_at: z.string().nullable() }),
});
const Locked = z.object({ available: z.literal(false) });
const Open = z.object({ available: z.literal(true), enabled: z.boolean().nullable() }).passthrough();
const ApiSettings = z.object({
  language: z.enum(['ru', 'en', 'de']),
  selected_coins: z.array(z.string()),
  watch_only_coins: z.array(z.string()),
  available_coins: z.array(z.string()),
  deliveries_running: z.boolean(),
  coin_news: z.union([ApiNewsSetting, Locked]),
  summary: z.union([ApiNewsSetting, Locked]),
  global: z.union([ApiNewsSetting, Locked]),
  macro: z.union([ApiNewsSetting, Locked]),
  calendar: z.union([Open, Locked]),
  forecast: z.union([Open, Locked]),
  investment_ideas: z.union([Open, Locked]),
  price_alerts: z.union([Open, Locked]),
  orcawatch: z.union([Open, Locked]),
  x_radar: z.union([Open, Locked]),
  youtube: z.union([Open, Locked]),
  wall_street: z.union([Open, Locked]),
}).passthrough();
type ApiSettingsT = Out<typeof ApiSettings>;

const ApiLot = z.object({
  id: z.string(), quantity: z.number(), price: z.number().nullable(),
  price_source: z.enum(['user', 'history_close', 'history_median']).nullable(),
  date: z.string().nullable(), date_source: z.enum(['user', 'history']).nullable(),
  period: z.string().nullable(), origin: z.enum(['web', 'telegram_position']),
});
const ApiHolding = z.object({
  symbol: z.string(), source: z.enum(['web', 'telegram']),
  quantity: z.number(), priced_quantity: z.number(), cost_basis: z.number(), average_price: z.number().nullable(),
  estimated: z.boolean(), incomplete: z.boolean(), since: z.string().nullable(),
  since_source: z.enum(['user', 'history', 'period']).nullable(),
  price: z.number().nullable(), change_24h_pct: z.number().nullable(), value: z.number().nullable(),
  unrealized_pl: z.number().nullable(), unrealized_pl_pct: z.number().nullable(), day_change: z.number().nullable(),
  share_pct: z.number().nullable(), lots: z.array(ApiLot),
});
const ApiPortfolio = z.object({
  holdings: z.array(ApiHolding),
  updated_at: z.string(),
  prices_at: z.string().nullable(),
  totals: z.object({
    value: z.number(), cost_basis: z.number(), unrealized_pl: z.number().nullable(), unrealized_pl_pct: z.number().nullable(),
    day_change: z.number(), day_change_pct: z.number().nullable(), estimated: z.boolean(),
    incomplete: z.array(z.string()), unpriced: z.array(z.string()),
  }),
});
const ApiCheck = z.object({
  status: z.enum(['single_match', 'multiple_matches', 'no_match', 'unavailable']),
  candidates: z.array(z.object({ day: z.string(), low: z.number(), high: z.number() })),
});
const ApiEstimate = z.object({
  status: z.enum(['ok', 'unavailable', 'not_traded']),
  method: z.enum(['close', 'median_close']).nullable(),
  price: z.number().nullable(), low: z.number().nullable(), high: z.number().nullable(), listed: z.string().nullable(),
});
const ApiQuotes = z.object({
  quotes: z.array(z.object({ symbol: z.string(), price: z.number(), change_24h_pct: z.number(), at: z.string() })),
  unavailable: z.array(z.string()),
});
const ApiNewsItem = z.object({
  id: z.number(), section: z.string().nullable(), category: z.string(), title: z.string(), summary: z.string(),
  url: z.string(), source: z.string().nullable(), image: z.string().nullable(), coins: z.array(z.string()),
  published_at: z.string(), telegram_included_at: z.string().nullable(), read: z.boolean(),
});
const ApiNews = z.object({
  language: z.string(), generated_at: z.string(), window_days: z.number(), last_read_at: z.string().nullable(),
  sections: z.record(z.object({ available: z.boolean() }).passthrough()),
  items: z.array(ApiNewsItem),
});
const Ok = z.object({ ok: z.literal(true) });

/* Phase 6B: profile, sessions, subscription, payments, Orca AI */
const ApiLinked = z.object({ linked: z.boolean(), linked_at: z.string().nullable().optional() });
const ApiProfile = z.object({
  account_id: z.number(), status: z.string(), created_at: z.string().nullable(), web_access_since: z.string().nullable(),
  identities: z.object({
    email: z.object({ address: z.string(), verified: z.boolean(), verified_at: z.string().nullable() }).nullable(),
    google: ApiLinked, telegram: ApiLinked,
  }),
});
const ApiSessions = z.object({ sessions: z.array(z.object({ id: z.string(), created_at: z.string().nullable(), expires_at: z.string().nullable(), current: z.boolean() })) });
const ApiPayStatus = z.enum(['paid', 'pending', 'failed', 'canceled', 'expired', 'annulled', 'refunded', 'unknown']);
const ApiMethod = z.enum(['card', 'crypto']);
const ApiPayments = z.object({ payments: z.array(z.object({
  id: z.string(), plan: z.string(), period: z.string(), months: z.number().nullable(), amount: z.number().nullable(), currency: z.string().nullable(),
  method: ApiMethod, status: ApiPayStatus, created_at: z.string().nullable(), paid_at: z.string().nullable(), active_until: z.string().nullable(),
})) });
const ApiSubscription = z.object({
  plan: PlanKey.nullable(), status: z.enum(['none', 'trial', 'free', 'paused', 'active']), period: z.string().nullable(),
  started_at: z.string().nullable(), active_until: z.string().nullable(),
  trial: z.object({ active: z.boolean(), used: z.boolean(), available: z.boolean(), ends_at: z.string().nullable(), days: z.number() }),
  free_status: z.string().nullable(),
  paid_access: z.object({ plan: z.string(), active_until: z.string().nullable() }).nullable(),
  can_buy: z.record(z.enum(['new', 'renew', 'change_required'])),
  pending_payment: z.object({ id: z.string(), plan: z.string(), period: z.string(), method: ApiMethod, created_at: z.string().nullable(), amount: z.number().nullable().optional() }).nullable(),
});
const ApiPlans = z.object({ plans: z.array(z.object({ id: z.enum(['plus', 'premium']), name: z.string(), periods: z.array(z.object({ period: z.string(), months: z.number(), amount_usd: z.number() })) })) });
const ApiCheckout = z.object({ id: z.string(), status: z.literal('pending'), checkout_url: z.string().url() }).passthrough();
const ApiPaymentCheck = z.object({ id: z.string(), plan: z.string(), status: ApiPayStatus, applied: z.boolean(), active_until: z.string().nullable() }).passthrough();
const ApiOutcome = z.object({ outcome: z.string() });
export type SendNowOutcome = 'sent' | 'empty' | 'busy' | 'queued';
const ApiRevoked = z.object({ sessions: z.array(z.unknown()) }).passthrough();
export const ApiQuota = z.object({ used: z.number(), limit: z.number(), remaining: z.number(), resets_at: z.string(), plan_required: z.boolean().optional() });
const ApiAiContext = z.object({ kind: z.string(), label: z.string().optional(), tag: z.string().optional(), reference_id: z.string().optional() }).passthrough();
const ApiAiStart = z.object({ request_id: z.string(), status: z.literal('pending'), conversation_id: z.string().uuid(), message_id: z.string().uuid() });
const ApiAiResult = z.object({ request_id: z.string(), status: z.string(), reply_html: z.string().optional(), quota: ApiQuota.optional() });
const ApiAiConversation = z.object({
  id: z.string().uuid(), title: z.string(), created_at: z.string(), last_activity_at: z.string(), expires_at: z.string(),
  message_count: z.number().optional(),
});
const ApiAiMessage = z.object({
  id: z.string().uuid(), role: z.enum(['user', 'assistant']), content: z.string(), context: ApiAiContext.nullable(),
  generation_id: z.string().nullable(), generation_status: z.enum(['pending', 'done', 'error']).nullable(), created_at: z.string(),
});
const ApiAiConversationList = z.object({ items: z.array(ApiAiConversation), retention_days: z.literal(15) });
const ApiAiConversationDetail = ApiAiConversation.omit({ message_count: true }).extend({ messages: z.array(ApiAiMessage) });
const ApiAvatar = z.object({ avatar: z.string().nullable(), updated_at: z.string().nullable() });
export type AvatarView = z.output<typeof ApiAvatar>;
const ApiWebPreferences = z.object({ tour_completed: z.boolean(), should_show_tour: z.boolean() });
export type AiQuota = Out<typeof ApiQuota>;
export type AiResult = Out<typeof ApiAiResult>;
export type AiContextDescriptor = Out<typeof ApiAiContext>;
export type AiConversation = Out<typeof ApiAiConversation>;
export type AiConversationDetail = Out<typeof ApiAiConversationDetail>;
export type WebPreferences = Out<typeof ApiWebPreferences>;

/* ---------- errors the UI can explain ---------- */
const KNOWN_ERRORS = ['coin_limit', 'free_needs_one_coin', 'unknown_coin', 'plan_required', 'feature_locked', 'invalid_interval',
  'interval_fixed', 'coin_not_tracked', 'too_many_lots', 'position_changed_in_telegram', 'date_not_in_history',
  'estimate_unavailable', 'estimate_not_traded', 'lot_not_found', 'telegram_only',
  'plan_change_required', 'rate_limited', 'payment_not_found', 'trial_used', 'trial_already_active', 'not_paid',
  'bridge_unavailable', 'bridge_timeout', 'bridge_failed', 'session_not_found', 'quota_exhausted', 'ai_busy', 'ai_unavailable'] as const;

/** An API failure as a message for a toast / form, in the account's language. */
export function apiMessage(error: unknown): string {
  const code = error instanceof HttpError ? error.code : '';
  if ((KNOWN_ERRORS as readonly string[]).includes(code)) return i18n.t(`server:apiErr.${code}`);
  if (error instanceof HttpError && error.kind === 'offline') return i18n.t('nav:error.offline');
  return i18n.t('server:apiErr.generic');
}
/** rethrow as an Error whose message the UI can show as is */
const explain = async <T>(p: Promise<T>): Promise<T> => {
  try { return await p; } catch (e) { const err = new Error(apiMessage(e)); (err as Error & { cause?: unknown }).cause = e; throw err; }
};

const write = <T extends z.ZodTypeAny>(method: 'POST' | 'PUT' | 'PATCH' | 'DELETE', path: string, schema: T, body?: unknown) =>
  requestJson(path, { method, body, csrf: getCsrf() ?? undefined, schema });

/* ---------- account ---------- */
const PLAN_NAME: Record<string, string> = { free: 'Free', essential: 'Essential', plus: 'Plus', premium: 'Premium' };
function nextUtcMidnight() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)).toISOString();
}

/** Web-only display time zone (never sent to the account / Telegram); this browser's zone by default. */
export const displayTimezone = {
  get(): string {
    try { const v = localStorage.getItem('orca-web-tz'); if (v) return v; } catch { /* storage unavailable */ }
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  },
  set(tz: string) { try { localStorage.setItem('orca-web-tz', tz); } catch { /* storage unavailable */ } },
};

const srv = (key: string, opts?: Record<string, unknown>) => i18n.t(`server:${key}`, opts);
const planSuffix = (plan: string | null) => (plan === 'premium' ? 'descPremium' : plan === 'plus' ? 'descPlus' : 'descFree');

function newsDelivery(key: string, s: ApiSettingsT['coin_news'], plan: string | null): Delivery | null {
  if (!s.available) return null;
  const hours = s.interval_hours ?? 0;
  const control: Delivery['control'] = s.interval_options.length > 1
    ? { type: 'steps', options: s.interval_options, value: hours }
    : { type: 'fixed', label: srv('control.every', { h: hours }) };
  return {
    key, icon: { coin: 'news', ov: 'home', macro: 'macro', global: 'radar' }[key] ?? 'news',
    title: srv(`delivery.${key}.title`), description: srv(`delivery.${key}.${planSuffix(plan)}`),
    enabled: s.enabled !== false, control, ...(s.schedule.next_run_at ? { nextAt: s.schedule.next_run_at } : {}),
  };
}

const WS_DIGESTS = ['pre', 'post', 'both'] as const;

/** The other shared deliveries (Phase 6C): the account's real state, changed through the same
 *  settings API as the news (the bot applies a Web change to the account's existing jobs).
 *  Investment ideas have no switch in the bot on Plus and stay as shown. */
function featureDeliveries(s: ApiSettingsT, plan: string | null): Delivery[] {
  const out: Delivery[] = [];
  const row = (key: string, icon: string, enabled: boolean | null, control: Delivery['control'], desc = 'desc', extra: Partial<Delivery> = {}): Delivery =>
    ({ key, icon, title: srv(`delivery.${key}.title`), description: srv(`delivery.${key}.${desc}`), enabled: enabled !== false, control, ...extra });
  const g = <K extends keyof ApiSettingsT>(k: K) => s[k] as { available: boolean; enabled?: boolean | null; mode?: string; min_importance?: number; digest?: string };
  if (g('x_radar').available) out.push(row('x', 'x', g('x_radar').enabled ?? null, { type: 'choice', options: [srv('control.instant'), srv('control.hourly')], value: g('x_radar').mode === 'hourly' ? 1 : 0 }));
  if (g('wall_street').available) {
    const i = WS_DIGESTS.indexOf((g('wall_street').digest ?? 'both') as typeof WS_DIGESTS[number]);
    out.push(row('ws', 'ws', g('wall_street').enabled ?? null, { type: 'choice', options: [srv('control.wsPre'), srv('control.wsPost'), srv('control.bothIssues')], value: i < 0 ? 2 : i }));
  }
  if (g('price_alerts').available) out.push(row('price', 'price', g('price_alerts').enabled ?? null, { type: 'fixed', label: srv('control.check30') }));
  if (g('orcawatch').available) out.push(row('whale', 'whale', g('orcawatch').enabled ?? null, { type: 'fixed', label: srv('control.instant') }));
  if (g('youtube').available) out.push(row('yt', 'yt', g('youtube').enabled ?? null, { type: 'fixed', label: srv('control.onRelease') }));
  if (g('investment_ideas').available) out.push(row('ideas', 'ai', g('investment_ideas').enabled ?? true, { type: 'fixed', label: srv('control.daily') }, planSuffix(plan), { readOnly: true, ...(plan === 'plus' ? { toggle: false } : {}) }));
  if (g('calendar').available) out.push(row('cal', 'cal', g('calendar').enabled ?? null, { type: 'choice', options: [srv('control.imp2'), srv('control.imp3')], value: g('calendar').min_importance === 3 ? 1 : 0 }));
  return out;
}

/** a Delivery change → the settings API body (same values as the bot's menus) */
function featurePatch(key: string, p: { enabled?: boolean; value?: number }): Record<string, unknown> | null {
  const on = p.enabled !== undefined ? { enabled: p.enabled } : {};
  switch (key) {
    case 'x': return { x_radar: { ...on, ...(p.value !== undefined ? { mode: p.value === 1 ? 'hourly' : 'instant' } : {}) } };
    case 'ws': return { wall_street: { ...on, ...(p.value !== undefined ? { digest: WS_DIGESTS[p.value] ?? 'both' } : {}) } };
    case 'cal': return { calendar: { ...on, ...(p.value !== undefined ? { min_importance: p.value === 1 ? 3 : 2 } : {}) } };
    case 'price': return { price_alerts: on };
    case 'whale': return { orcawatch: on };
    case 'yt': return { youtube: on };
    default: return null;
  }
}

function toAccount(a: Out<typeof ApiAccount>, e: Out<typeof ApiEntitlements>, s: ApiSettingsT): Account {
  const plan = e.plan === 'essential' ? 'free' : e.plan;
  const news = [newsDelivery('coin', s.coin_news, plan), newsDelivery('ov', s.summary, plan), newsDelivery('macro', s.macro, plan), newsDelivery('global', s.global, plan)]
    .filter((d): d is Delivery => d !== null);
  return {
    user: { name: '' },
    plan: { id: plan ?? 'free', name: PLAN_NAME[plan ?? 'free'], coinLimit: e.limits.coins, aiDailyLimit: e.limits.ai_daily, activeUntil: a.active_until },
    capabilities: e.capabilities,
    trial: { available: plan === 'free' && !a.trial.used, days: e.trial_days },
    coinLimits: { plus: e.plan_limits.plus?.coins ?? 5, premium: e.plan_limits.premium?.coins ?? 8 },
    planLimits: {
      free: { coins: e.plan_limits.free?.coins ?? 1, ai: e.plan_limits.free?.ai_daily ?? 3 },
      plus: { coins: e.plan_limits.plus?.coins ?? 5, ai: e.plan_limits.plus?.ai_daily ?? 30 },
      premium: { coins: e.plan_limits.premium?.coins ?? 8, ai: e.plan_limits.premium?.ai_daily ?? 55 },
    },
    usage: { aiToday: null, resetAt: nextUtcMidnight() },
    coins: s.selected_coins,
    availableCoins: s.available_coins,
    language: s.language,
    deliveries: [...news, ...featureDeliveries(s, plan)],
    web: { displayTimezone: displayTimezone.get() },
  };
}

async function fetchAccount(): Promise<Account> {
  const [a, e, s] = await Promise.all([
    requestJson('/api/v1/account', { schema: ApiAccount }),
    requestJson('/api/v1/entitlements', { schema: ApiEntitlements }),
    requestJson('/api/v1/settings', { schema: ApiSettings }),
  ]);
  return toAccount(a, e, s);
}

const DELIVERY_API: Record<string, string> = { coin: 'coin_news', ov: 'summary', global: 'global', macro: 'macro' };

async function patchSettings(body: Record<string, unknown>): Promise<Account> {
  await explain(write('PATCH', '/api/v1/settings', ApiSettings, body));
  return fetchAccount();
}

/* ---------- portfolio ---------- */
function toLot(l: Out<typeof ApiLot>): PurchaseLot {
  return {
    id: l.id, qty: l.quantity,
    ...(l.price !== null ? { price: l.price, priceSource: l.price_source ?? 'user' } : {}),
    ...(l.date ? { date: l.date, dateSource: l.date_source ?? 'user' } : {}),
    ...(l.period ? { period: l.period } : {}),
    ...(l.origin !== 'web' ? { origin: l.origin } : {}),
  };
}

function toPortfolio(p: Out<typeof ApiPortfolio>): Portfolio {
  return {
    updatedAt: p.prices_at ?? p.updated_at,
    holdings: p.holdings.map((h) => ({
      symbol: h.symbol, qty: h.quantity, avgPrice: h.average_price ?? 0, cost: h.cost_basis, pricedQty: h.priced_quantity,
      ...(h.estimated ? { estimated: true } : {}), ...(h.incomplete ? { incomplete: true } : {}),
      ...(h.since ? { since: h.since, sinceSource: h.since_source ?? 'user' } : {}),
      lots: h.lots.map(toLot), source: h.source,
      valuation: { price: h.price, change24h: h.change_24h_pct, value: h.value, pnl: h.unrealized_pl, pnlPct: h.unrealized_pl_pct, day: h.day_change, share: h.share_pct },
    })),
    totals: {
      value: p.totals.value, cost: p.totals.cost_basis, pnl: p.totals.unrealized_pl, pnlPct: p.totals.unrealized_pl_pct,
      day: p.totals.day_change, dayPct: p.totals.day_change_pct, estimated: p.totals.estimated,
      incomplete: p.totals.incomplete, unpriced: p.totals.unpriced,
    },
  };
}

const lotBody = (symbol: string, lot: Omit<PurchaseLot, 'id'>) => ({
  symbol, quantity: lot.qty,
  ...(lot.price !== undefined ? { price: lot.price, price_source: lot.priceSource ?? 'user' } : {}),
  ...(lot.date ? { date: lot.date, date_source: lot.dateSource ?? 'user' } : {}),
  ...(lot.period ? { period: lot.period } : {}),
});

/* ---------- news ---------- */
const CATEGORY: Record<string, NewsItem['category']> = { coins: 'crypto', crypto: 'crypto', stock: 'stocks', global: 'world', macro: 'macro' };
// the article's own picture from its feed, or none: the card then shows ORCA's
// fallback — never a stock landscape that would pass for the article's photo
function toNewsItem(i: Out<typeof ApiNewsItem>): NewsItem {
  const category = CATEGORY[i.category] ?? 'world';
  return {
    id: i.id, at: i.published_at, category, tags: i.coins, title: i.title, dek: i.summary, body: '',
    image: i.image ?? '', isNew: !i.read,
    source: { name: i.source ?? new URL(i.url).hostname, url: i.url }, alsoIn: [],
    section: i.section ?? undefined, telegramIncludedAt: i.telegram_included_at,
  };
}


const ApiNewsPage = ApiNews.extend({
  page: z.number(), per_page: z.number(), pages: z.number(), total: z.number(),
  facets: z.object({ categories: z.record(z.string(), z.number()), coins: z.record(z.string(), z.number()), days: z.array(z.number()),
    unread: z.number(), telegram: z.number(), today: z.number(), feed: z.number() }),
  summary_top: z.array(ApiNewsItem),
});
export type NewsQuery = { scope: 'latest' | 'today' | 'week'; coin: string; cat: 'all' | NewsItem['category']; page: number; perPage: number; ids?: number[] };

/** one page of the News page, filtered and counted by the server (in the display time zone) */
async function fetchNewsPage(q: NewsQuery): Promise<NewsPage> {
  const qs = new URLSearchParams({ page: String(q.page), per_page: String(q.perPage), scope: q.scope, coin: q.coin, cat: q.cat, tz: displayTimezone.get() });
  if (q.ids?.length) qs.set('ids', q.ids.join(','));
  const n = await requestJson(`/api/v1/news?${qs}`, { schema: ApiNewsPage });
  return {
    items: n.items.map(toNewsItem), page: n.page, perPage: n.per_page, pages: n.pages, total: n.total, facets: n.facets,
    summaryTop: n.summary_top.map(toNewsItem), lastVisit: n.last_read_at ?? n.generated_at, updatedAt: n.generated_at,
  };
}

/* ---------- profile, subscription, payments ---------- */
const PERIOD_OF: Record<number, string> = { 1: 'monthly', 3: '3m', 6: '6m', 12: '12m' };
const DAY = 86_400_000;
const periodLabel = (months: number | null | undefined) => (months ? srv('profile.months', { count: months }) : '—');
const methodLabel = (m: string) => srv(`profile.method.${m}`);
const usdLabel = (n: number | null | undefined) => (n == null ? '—' : fmtPrice(n));
const MONTHS: Record<string, number> = { monthly: 1, '3m': 3, '6m': 6, '12m': 12 };
/** a payment that changes nothing in the UI's own words: only the webhook (server) decides */
class PaymentStateError extends Error { constructor(message: string, readonly code: string) { super(message); } }

function toProfile(p: Out<typeof ApiProfile>, ses: Out<typeof ApiSessions>, sub: Out<typeof ApiSubscription>, pays: Out<typeof ApiPayments>): Profile {
  const now = Date.now();
  const until = sub.active_until ? Date.parse(sub.active_until) : null;
  const daysLeft = until ? Math.max(0, Math.ceil((until - now) / DAY)) : 0;
  const started = sub.started_at ? Date.parse(sub.started_at) : null;
  const plan = sub.plan === 'essential' || !sub.plan ? 'free' : sub.plan;
  // the bot reminds 3 days and 1 day before the end: the same window here
  const status: Profile['subscription']['status'] = sub.status === 'active' ? (daysLeft <= 3 ? 'expiring' : 'active')
    : sub.status === 'trial' ? 'trial' : sub.status === 'paused' ? 'paused' : 'free';
  const pend = sub.pending_payment;
  return {
    accountId: p.account_id,
    timezone: displayTimezone.get(),
    email: p.identities.email ? { address: p.identities.email.address, verified: p.identities.email.verified } : null,
    telegram: p.identities.telegram.linked ? { linkedAt: p.identities.telegram.linked_at ?? null } : null,
    google: p.identities.google.linked ? { linkedAt: p.identities.google.linked_at ?? null } : null,
    sessions: ses.sessions.map((x) => ({ id: x.id, createdAt: x.created_at, expiresAt: x.expires_at, current: x.current })),
    billing: { payments: pays.payments.map((x) => ({
      id: x.id, at: x.paid_at ?? x.created_at ?? '', plan: PLAN_NAME[x.plan] ?? x.plan, period: periodLabel(x.months),
      amount: usdLabel(x.amount), method: methodLabel(x.method), status: x.status,
    })) },
    subscription: {
      status, plan: PLAN_NAME[status === 'free' || status === 'paused' ? 'free' : plan] ?? 'Free',
      period: sub.status === 'active' ? periodLabel(MONTHS[sub.period ?? '']) : sub.status === 'trial' ? srv('profile.period.trial') : srv('profile.period.free'),
      endsAt: sub.status === 'active' || sub.status === 'trial' ? sub.active_until : null,
      daysLeft,
      days: sub.status === 'trial' ? sub.trial.days : until && started ? Math.max(1, Math.round((until - started) / DAY)) : 0,
      ...(pend ? { pending: { id: pend.id, amount: usdLabel(pend.amount), method: methodLabel(pend.method), since: pend.created_at ?? new Date(now).toISOString() } } : {}),
      trialUsed: sub.trial.used,
    },
    referral: { bot: 'https://t.me/ReferralOrca_bot' },
    createdAt: p.created_at,
  };
}

async function fetchProfile(): Promise<Profile> {
  const [p, ses, sub, pays] = await Promise.all([
    requestJson('/api/v1/profile', { schema: ApiProfile }),
    requestJson('/api/v1/profile/sessions', { schema: ApiSessions }),
    requestJson('/api/v1/profile/subscription', { schema: ApiSubscription }),
    requestJson('/api/v1/profile/payments', { schema: ApiPayments }),
  ]);
  return toProfile(p, ses, sub, pays);
}

async function fetchPlans(): Promise<PlanOffer[]> {
  const r = await requestJson('/api/v1/plans', { schema: ApiPlans });
  return r.plans.map((x) => ({
    id: x.id, name: x.name, note: srv(`profile.offer.${x.id}`),
    prices: x.periods.map((q) => ({ months: q.months, price: q.amount_usd, perMonth: Math.round((q.amount_usd / q.months) * 100) / 100, period: q.period })),
  }));
}

/** the payment's state after the return from checkout, from the server (the webhook's result) */
async function checkPayment(id: string): Promise<Profile> {
  const r = await explain(write('POST', `/api/v1/payments/${encodeURIComponent(id)}/check`, ApiPaymentCheck));
  if (r.status === 'paid' && r.applied) return fetchProfile();
  if (r.status === 'paid') throw new PaymentStateError(srv('profile.err.applying'), 'pending');
  if (r.status === 'pending') throw new PaymentStateError(srv('profile.err.pendingReal'), 'pending');
  if (r.status === 'canceled') throw new PaymentStateError(srv('profile.err.canceled'), 'canceled');
  if (r.status === 'expired') throw new PaymentStateError(srv('profile.err.expired'), 'expired');
  throw new PaymentStateError(srv('profile.err.failed'), 'failed');
}

/* ---------- Phase 6C: calendar, Wall Street, alerts, OrcaWatch, social, ideas, bell ---------- */
const N = <T extends z.ZodTypeAny>(t: T) => t.nullable();
const ApiCalendar = z.object({
  from: z.string(), days: z.number(), min_importance: z.number(), account_min_importance: z.number(), enabled: z.boolean().nullable(),
  events: z.array(z.object({
    id: z.string(), at: z.string(), country: N(z.string()), currency: N(z.string()), flag: N(z.string()), importance: z.number(),
    title: z.string(), title_long: N(z.string()), period: N(z.string()), unit: N(z.string()),
    actual: N(z.string()), forecast: N(z.string()), previous: N(z.string()),
  })),
});
const ApiWsPart = z.object({
  at: z.string(), summary: z.string(),
  prices: z.array(z.object({ key: z.string(), label: z.string(), price: z.number(), change_pct: z.number() })),
  news: z.array(z.object({ title: z.string(), url: z.string(), source: N(z.string()), category: N(z.string()) })).optional(),
});
const ApiWallStreet = z.object({
  enabled: z.boolean().nullable(), digest: z.string(),
  schedule: z.object({ pre: z.string(), post: z.string(), tz: z.string() }),
  days: z.array(z.object({ trading_date: z.string(), pre: N(ApiWsPart), post: N(ApiWsPart) })),
});
const ApiWallet = z.object({ label: N(z.string()), type: z.enum(['exchange', 'wallet', 'unknown']) });
const ApiOrcaWatch = z.object({
  enabled: z.boolean(), networks: z.array(z.string()), threshold_usd: z.number(), max_networks: z.number(),
  options: z.object({ networks: z.array(z.object({ value: z.string(), label: z.string() })), thresholds: z.array(z.number()) }),
  feed: z.array(z.object({
    id: z.string(), at: N(z.string()), network: z.string(), token: N(z.string()), amount: z.number(), usd: z.number(),
    from: ApiWallet, to: ApiWallet, flow: z.enum(['inflow', 'outflow', 'exchange_to_exchange', 'wallet_transfer']),
    hash: z.string(), url: N(z.string()),
  })),
});
const ApiPriceAlerts = z.object({
  enabled: z.boolean(), levels: z.array(z.number()), basis: z.string(), delivery: z.enum(['telegram', 'web', 'unavailable']),
  coins: z.array(z.object({ symbol: z.string(), level: z.number(), known: z.boolean() })),
  history: z.array(z.object({ id: z.string(), symbol: z.string(), level: z.number(), change_pct: z.number(), price: z.number(), at: z.string() })),
});
const ApiPaging = { page: z.number().optional(), per_page: z.number().optional(), total: z.number().optional(), pages: z.number().optional() };
const ApiSocial = z.object({
  x: z.union([z.object({ available: z.literal(false) }), z.object({
    available: z.literal(true), enabled: z.boolean().nullable(), mode: z.string(),
    disabled_reason: z.string().optional(),
    posts: z.array(z.object({ id: z.number(), at: N(z.string()), author: N(z.string()), text: z.string(), coins: z.array(z.string()), mine: z.boolean(), url: z.string() })),
    ...ApiPaging, coin_counts: z.record(z.string(), z.number()).optional(),
  })]).optional(),
  youtube: z.union([z.object({ available: z.literal(false) }), z.object({
    available: z.literal(true), enabled: z.boolean().nullable(),
    disabled_reason: z.string().optional(),
    videos: z.array(z.object({ id: z.number(), at: N(z.string()), title: z.string(), url: z.string(), image: z.string(), coins: z.array(z.string()), mine: z.boolean() })),
    ...ApiPaging,
  })]).optional(),
});
type ApiSocialT = Out<typeof ApiSocial>;
const toPosts = (x: ApiSocialT['x']) => (x?.available ? x.posts.filter((p) => p.at).map((p) => ({
  at: p.at!, who: p.author ? `@${p.author}` : 'X', handle: p.author ?? '', symbol: p.coins[0] ?? '', text: p.text,
  coins: p.coins, mine: p.mine, url: p.url,
})) : []);
const toVideos = (y: ApiSocialT['youtube']) => (y?.available ? y.videos.filter((v) => v.at).map((v) => ({
  image: v.image, title: v.title, at: v.at!, symbols: v.coins, url: v.url,
})) : []);
const ApiIdeas = z.object({
  enabled: z.boolean().nullable(), per_delivery: z.number(),
  items: z.array(z.object({ id: z.number(), at: N(z.string()), title: z.string(), summary: z.string(), url: z.string(), source: N(z.string()),
    coins: z.array(z.string()), telegram_included_at: N(z.string()) })),
});
const ApiNotices = z.object({
  unread: z.number(),
  items: z.array(z.object({
    id: z.string(), kind: z.enum(['price', 'whale', 'digest', 'calendar', 'payment', 'forecast']), urgent: z.boolean(), at: z.string(),
    to: z.string(), read: z.boolean(), symbol: z.string().nullable().optional(), dir: z.enum(['up', 'down']).optional(),
    data: z.record(z.unknown()),
  })),
});
const ApiMarked = z.object({ ok: z.literal(true), marked: z.number() });

/* Phase 6D: stored forecasts, Signals, portfolio history */
const ApiForecast = z.object({
  id: z.string(), asset: z.string(), depth: z.string(), language: z.string(), text: z.string(), position_note: N(z.string()),
  horizon_days: z.number(), generated_at: z.string(), valid_until: z.string(), stale: z.boolean(), telegram_sent_at: N(z.string()),
  read: z.boolean(), tracked: z.boolean(),
});
const ApiSignal = z.object({
  asset: z.string(), forecast: N(ApiForecast), forecast_locked: z.boolean(),
  series: z.array(z.tuple([z.string(), z.number()])), series_unavailable: z.boolean(),
  upcoming: z.array(ApiCalendar.shape.events.element), upcoming_locked: z.boolean(),
});
const ApiHistory = z.object({ days: z.array(z.object({
  day: z.string(), value: z.number(), cost_basis: z.number(), unrealized_pl: N(z.number()), assets: z.number(),
  priced_assets: z.number(), incomplete: z.boolean() })) });
export type HistoryPoint = z.infer<typeof ApiHistory>['days'][number];

/** a feature the plan does not open answers 403: an empty result, never an error page */
async function orLocked<T>(p: Promise<T>, locked: () => T): Promise<T> {
  try { return await p; } catch (e) {
    if (e instanceof HttpError && e.status === 403) return locked();
    throw e;
  }
}
const mondayUtc = () => {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
  return d.toISOString().slice(0, 10);
};

async function fetchCalendar(): Promise<Calendar> {
  const weekStart = mondayUtc();
  // every event the bot can deliver (2+); the account's reminder threshold is a setting, not a filter of the page
  const r = await orLocked(requestJson(`/api/v1/calendar?from=${weekStart}&days=7&min_importance=2`, { schema: ApiCalendar }), () => null);
  const events: CalEvent[] = (r?.events ?? []).map((e) => ({
    id: e.id, at: e.at, title: e.period ? `${e.title} (${e.period})` : e.title, short: e.title,
    region: [e.country, e.currency].filter(Boolean).join(' · '), assets: e.currency ? [e.currency] : [],
    imp: Math.max(1, Math.min(3, e.importance)),
    ...(e.previous ? { prev: e.previous } : {}), ...(e.forecast ? { fc: e.forecast } : {}), ...(e.actual ? { fact: e.actual } : {}),
  }));
  return { weekStart, events };
}

const INDEX_KEYS = ['sp500', 'dow', 'nasdaq'];
const RATE_KEYS: Record<string, string> = { us2y: '%', us10y: '%', dxy: '', wti: '$', gold: '$' };
function toDigest(part: z.infer<typeof ApiWsPart> | null, tradingDate: string | undefined, fallbackAt: string): Digest {
  const fmt = (n: number) => nf(n, 2);
  const prices = part?.prices ?? [];
  const byKey = (k: string) => prices.find((x) => x.key === k);
  const indices = [...INDEX_KEYS, 'vix'].map(byKey).filter((x): x is NonNullable<typeof x> => !!x)
    .map((x) => [x.label, fmt(x.price), x.change_pct] as [string, string, number]);
  const rates = Object.entries(RATE_KEYS).map(([k, unit]) => [byKey(k), unit] as const).filter(([x]) => !!x)
    .map(([x, unit]) => [x!.label, fmt(x!.price), unit, x!.change_pct] as [string, string, string, number]);
  return {
    at: part?.at ?? fallbackAt, lede: part?.summary ?? '', indices, themes: [], movers: [], earnings: [], macro: [], sectors: [], rates,
    news: (part?.news ?? []).map((n) => ({ title: n.title, url: n.url, source: n.source })),
    ...(part ? {} : { missing: true }), ...(tradingDate ? { tradingDate } : {}),
  };
}

async function fetchWallStreet(): Promise<WallStreet> {
  const r = await orLocked(requestJson('/api/v1/wall-street', { schema: ApiWallStreet }), () => null);
  const schedule = r?.schedule ?? { pre: '09:00', post: '16:30', tz: 'America/New_York' };
  const pre = r?.days.find((d) => d.pre);
  const post = r?.days.find((d) => d.post);
  const now = new Date().toISOString();
  return { pre: toDigest(pre?.pre ?? null, pre?.trading_date, now), post: toDigest(post?.post ?? null, post?.trading_date, now), schedule };
}

const NETWORK_TOKEN: Record<string, string> = {
  'Ethereum Mainnet': 'ETH', 'Arbitrum One': 'ARB', 'BNB Smart Chain': 'BNB', 'Avalanche C-Chain': 'AVAX', Polygon: 'POL',
  Optimism: 'OP', Base: 'BASE', Solana: 'SOL', Tron: 'TRX',
};
const CHANNELS_KEY = 'orca-web-alert-channels';
/** where urgent alerts show up on this site — a web-only preference (this browser) */
export const alertChannels = {
  get(): { site: boolean } {
    try { const v = JSON.parse(localStorage.getItem(CHANNELS_KEY) ?? 'null'); if (v && typeof v.site === 'boolean') return { site: v.site }; } catch { /* storage unavailable */ }
    return { site: true };
  },
  set(v: { site: boolean }) { try { localStorage.setItem(CHANNELS_KEY, JSON.stringify(v)); } catch { /* storage unavailable */ } },
};

async function fetchAlerts(): Promise<Alerts> {
  const [price, watch] = await Promise.all([
    orLocked(requestJson('/api/v1/alerts/price', { schema: ApiPriceAlerts }), () => null),
    orLocked(requestJson('/api/v1/orcawatch', { schema: ApiOrcaWatch }), () => null),
  ]);
  const feed: WhaleTx[] = (watch?.feed ?? []).filter((x) => x.at).map((x) => ({
    id: x.id, at: x.at!, network: x.network, token: x.token ?? '', amount: x.amount, usd: x.usd,
    from: { label: x.from.label ?? srv('alerts.unknown'), type: x.from.type }, to: { label: x.to.label ?? srv('alerts.unknown'), type: x.to.type },
    flow: x.flow, hash: x.hash, url: x.url ?? '',
  }));
  return {
    whale: {
      available: !!watch, enabled: watch?.enabled ?? false, networks: watch?.networks ?? [], maxNetworks: watch?.max_networks ?? 4,
      threshold: watch?.threshold_usd ?? 1_000_000,
      options: { networks: (watch?.options.networks ?? []).map((n) => ({ ...n, token: NETWORK_TOKEN[n.value] ?? n.label })), thresholds: watch?.options.thresholds ?? [] },
      feed,
    },
    price: {
      available: !!price, enabled: price?.enabled ?? false, delivery: price?.delivery,
      levels: price?.levels ?? [2, 5, 9, 14, 20, 25, 30, 35], basis: srv('alerts.basis'), check: srv('alerts.check'),
      coins: (price?.coins ?? []).map((c) => ({ symbol: c.symbol, level: c.level })),
      history: (price?.history ?? []).map((h) => ({ id: h.id, at: h.at, symbol: h.symbol, change: h.change_pct, price: h.price, level: h.level })),
    },
    channels: alertChannels.get(),
  };
}

async function fetchSocial(): Promise<Social> {
  const r = await requestJson('/api/v1/social', { schema: ApiSocial });
  // per-coin X activity (mentions, accounts, spikes) is not collected by ORCA
  return { posts: toPosts(r.x), videos: toVideos(r.youtube), coins: [] };
}

export const X_PAGE_SIZE = 25;
// video cards carry a thumbnail each: a lighter page than the text feeds
export const YT_PAGE_SIZE = 12;
export type SocialQuery = { page: number; perPage: number; coin: string | null; scope?: 'today' | 'week' };
const socialQs = (prefix: 'x' | 'yt', q: SocialQuery) =>
  `${prefix}_page=${q.page}&${prefix}_per_page=${q.perPage}&scope=${q.scope ?? 'week'}&tz=${encodeURIComponent(displayTimezone.get())}${q.coin ? `&${prefix}_coin=${encodeURIComponent(q.coin)}` : ''}`;

/** one server page of X Radar — the Social page never downloads the whole week */
async function fetchSocialX(q: SocialQuery): Promise<SocialXPage> {
  const r = await requestJson(`/api/v1/social?only=x&${socialQs('x', q)}`, { schema: ApiSocial });
  const x = r.x?.available ? r.x : null;
  return { available: !!x, enabled: x?.enabled !== false, ...(x?.disabled_reason ? { disabledReason: x.disabled_reason } : {}), posts: toPosts(r.x), page: x?.page ?? 1, pages: x?.pages ?? 1, total: x?.total ?? 0, perPage: q.perPage,
    counts: x?.coin_counts ?? {}, coins: [] };
}

async function fetchSocialYt(q: SocialQuery): Promise<SocialYtPage> {
  const r = await requestJson(`/api/v1/social?only=youtube&${socialQs('yt', q)}`, { schema: ApiSocial });
  const y = r.youtube?.available ? r.youtube : null;
  return { available: !!y, enabled: y?.enabled !== false, ...(y?.disabled_reason ? { disabledReason: y.disabled_reason } : {}), videos: toVideos(r.youtube), page: y?.page ?? 1, pages: y?.pages ?? 1, total: y?.total ?? 0, perPage: q.perPage };
}

async function fetchIdeas(): Promise<Idea[]> {
  const r = await orLocked(requestJson('/api/v1/ideas', { schema: ApiIdeas }), () => null);
  return (r?.items ?? []).filter((i) => i.at).map((i) => ({
    id: String(i.id), at: i.at!, title: i.title, dek: i.summary, tags: i.coins,
    source: { name: i.source ?? new URL(i.url).hostname, url: i.url },
  }));
}

/** the forecast's first sentences as the short note; the whole text as the full one */
const lead = (text: string) => text.split(/(?<=[.!?])\s+/).slice(0, 2).join(' ');
const DAY_MS = 86_400_000;

/** Signals: per coin the stored forecast, the real 24 h price and the week's calendar,
 *  with a timeline of the coin's real events from their own (plan-gated) feeds. */
async function fetchSignals(): Promise<Signals> {
  const settings = await requestJson('/api/v1/settings', { schema: ApiSettings });
  const coins = settings.selected_coins;
  // the coin's newest articles: a small server page per coin (never the whole 7-day feed)
  const [coinNews, social, alerts, ...signals] = await Promise.all([
    Promise.all(coins.map((c) => fetchNewsPage({ scope: 'week', coin: c, cat: 'all', page: 1, perPage: 15 }))),
    // X posts only (the timeline's last 24 h); YouTube is not a timeline event
    requestJson('/api/v1/social?only=x', { schema: ApiSocial }).then((r) => ({ posts: r.x?.available && r.x.enabled !== false ? toPosts(r.x) : [] })), fetchAlerts(),
    ...coins.map((c) => requestJson(`/api/v1/signals?asset=${encodeURIComponent(c)}`, { schema: ApiSignal })),
  ]);
  const newsOf = Object.fromEntries(coins.map((c, i) => [c, coinNews[i].items]));
  const now = Date.now();
  const recent = (iso: string) => now - Date.parse(iso) <= DAY_MS && Date.parse(iso) <= now;
  const assets: Record<string, AssetDay> = {};
  for (const sig of signals) {
    const coin = sig.asset;
    const ev: Omit<TimelineEvent, 'lane'>[] = [
      ...alerts.price.history.filter((a) => a.symbol === coin && recent(a.at)).map((a) => ({
        at: a.at, type: 'price' as const, title: `${coin} ${a.level > 0 ? '+' : ''}${a.level}%`, body: `${pct(a.change)} · ${fmtPrice(a.price)}` })),
      ...alerts.whale.feed.filter((w) => w.token === coin && recent(w.at)).map((w) => ({
        at: w.at, type: 'whale' as const, title: `${money(w.usd)} ${w.token}`, body: w.network, url: w.url, source: w.network })),
      ...social.posts.filter((p) => (p.coins ?? []).includes(coin) && recent(p.at)).map((p) => ({
        at: p.at, type: 'x' as const, title: p.text, body: p.handle ? `@${p.handle}` : 'X', url: p.url, source: p.handle ? `@${p.handle}` : 'X' })),
      ...(newsOf[coin] ?? []).filter((n) => recent(n.at)).map((n) => ({
        at: n.at, type: 'news' as const, title: n.title, body: n.source.name, url: n.source.url, source: n.source.name })),
    ].sort((a, b) => a.at.localeCompare(b.at));
    const f = sig.forecast;
    assets[coin] = {
      range: [new Date(now - DAY_MS).toISOString(), new Date(now).toISOString()],
      forecastText: f ? lead(f.text) : '', forecastFull: f?.text ?? '',
      forecastLocked: sig.forecast_locked, ...(!f && !sig.forecast_locked ? { forecastMissing: true } : {}),
      ...(f ? { forecastMeta: { generatedAt: f.generated_at, validUntil: f.valid_until, stale: f.stale, depth: f.depth,
        telegramSentAt: f.telegram_sent_at, positionNote: f.position_note } } : {}),
      series: sig.series,
      events: ev.map((e, i) => ({ ...e, lane: (i % 2) as 0 | 1 })),
      upcoming: sig.upcoming.map((e) => ({
        at: e.at, type: 'macro' as const, title: e.title, imp: Math.max(1, Math.min(3, e.importance)), calendarId: e.id,
        why: [[e.country, e.currency].filter(Boolean).join(' · '), e.forecast ? srv('calendar.forecast', { v: e.forecast }) : '',
          e.previous ? srv('calendar.previous', { v: e.previous }) : ''].filter(Boolean).join(' · '),
      })),
    };
  }
  return { assets };
}

/** the bell: real events of the account, worded here in the UI language */
function toNotice(n: z.infer<typeof ApiNotices>['items'][number]): Notice {
  const d = n.data as Record<string, unknown>;
  const num = (k: string) => (typeof d[k] === 'number' ? (d[k] as number) : 0);
  const str = (k: string) => (typeof d[k] === 'string' ? (d[k] as string) : '');
  const nt = (key: string, o?: Record<string, unknown>) => i18n.t(`notices:real.${key}`, o);
  const usd = money;
  let title = '', body = '';
  switch (n.kind) {
    case 'price': title = nt('priceTitle', { coin: n.symbol, level: pct(num('level')) }); body = nt('priceBody', { change: pct(num('change_pct')) }); break;
    case 'whale': title = nt('whaleTitle', { usd: usd(num('usd')), token: n.symbol ?? '' }); body = nt(`flow.${str('flow') || 'wallet_transfer'}`, { network: str('network') }); break;
    case 'digest': title = nt(str('part') === 'post' ? 'digestPost' : 'digestPre'); body = nt('digestBody', { date: str('trading_date') }); break;
    case 'calendar': title = str('title'); body = nt('calendarBody', { actual: str('actual'), forecast: str('forecast') || '—', previous: str('previous') || '—' }); break;
    case 'payment': title = nt('paymentTitle', { plan: str('plan') === 'premium' ? 'Premium' : 'Plus' }); body = nt('paymentBody'); break;
    case 'forecast': title = nt('forecastTitle', { coin: str('asset') }); body = nt('forecastBody'); break;
  }
  return { id: n.id, kind: n.kind, urgent: n.urgent, title, body, at: n.at, read: n.read, to: n.to,
    ...(n.dir ? { dir: n.dir } : {}), ...(n.symbol ? { symbol: n.symbol } : {}) };
}

/* ---------- the API ---------- */
export const realApi = {
  account: fetchAccount,
  setCoins: (coins: string[]) => patchSettings({ coins }),
  setLanguage: (language: Lang) => patchSettings({ language }),
  setDelivery: async (key: string, p: { enabled?: boolean; value?: number }) => {
    const name = DELIVERY_API[key];
    if (!name) {
      const body = featurePatch(key, p);
      if (!body) throw new Error(i18n.t('server:apiErr.telegram_only'));
      return patchSettings(body);
    }
    return patchSettings({ news: { [name]: { ...(p.enabled !== undefined ? { enabled: p.enabled } : {}), ...(p.value !== undefined ? { interval_hours: p.value } : {}) } } });
  },
  portfolio: async () => toPortfolio(await requestJson('/api/v1/portfolio', { schema: ApiPortfolio })),
  addLot: async (symbol: string, lot: PurchaseLot) => toPortfolio(await explain(write('POST', '/api/v1/portfolio/lots', ApiPortfolio, lotBody(symbol, lot)))),
  updateLot: async (symbol: string, id: string, lot: Omit<PurchaseLot, 'id'>) =>
    toPortfolio(await explain(write('PATCH', `/api/v1/portfolio/lots/${encodeURIComponent(id)}`, ApiPortfolio, lotBody(symbol, lot)))),
  removeLot: async (_symbol: string, id: string) => toPortfolio(await explain(write('DELETE', `/api/v1/portfolio/lots/${encodeURIComponent(id)}`, ApiPortfolio))),
  removePosition: async (symbol: string) => toPortfolio(await explain(write('DELETE', `/api/v1/portfolio/positions/${encodeURIComponent(symbol)}`, ApiPortfolio))),
  checkPeriod: async (symbol: string, price: number, period: string): Promise<HistoryCheck> => {
    const q = new URLSearchParams({ symbol, price: String(price), period });
    return requestJson(`/api/v1/portfolio/history/check?${q}`, { schema: ApiCheck });
  },
  estimatePrice: async (symbol: string, when: { day: string } | { period: string }): Promise<PriceEstimate> => {
    const q = new URLSearchParams({ symbol, ...when });
    const e = await requestJson(`/api/v1/portfolio/history/estimate?${q}`, { schema: ApiEstimate });
    return { status: e.status, ...(e.method ? { method: e.method } : {}), ...(e.price !== null ? { price: e.price } : {}),
      ...(e.low !== null ? { low: e.low } : {}), ...(e.high !== null ? { high: e.high } : {}), ...(e.listed ? { listed: e.listed } : {}) };
  },
  /** current quotes of every coin ORCA follows; a coin without a quote is an error, never a made-up price */
  universe: async () => {
    const q = await requestJson('/api/v1/market/quotes', { schema: ApiQuotes });
    if (q.unavailable.length) throw new HttpError(503, 'quotes_unavailable', null);
    return q.quotes.filter((x) => COINS[x.symbol]).map((x) => ({
      symbol: x.symbol, name: COINS[x.symbol].name, decimals: COINS[x.symbol].decimals, sparkSeed: COINS[x.symbol].sparkSeed,
      price: x.price, change24h: x.change_24h_pct,
    }));
  },
  newsPage: fetchNewsPage,
  markNewsRead: (ids: number[]) => write('POST', '/api/v1/news/read', Ok, { ids }),

  profile: fetchProfile,
  plans: fetchPlans,
  setTimezone: async (tz: string) => { displayTimezone.set(tz); return fetchProfile(); },
  endSession: async (id: string) => { await explain(write('DELETE', `/api/v1/profile/sessions/${encodeURIComponent(id)}`, ApiRevoked)); return fetchProfile(); },
  endOtherSessions: async () => { await explain(write('POST', '/api/v1/profile/sessions/revoke-others', ApiRevoked)); return fetchProfile(); },
  startTrial: async () => { await explain(write('POST', '/api/v1/subscription/trial', ApiOutcome)); return fetchProfile(); },
  /** «Прислать новости сейчас»: the bot builds and sends the news to Telegram (sent | empty | busy | queued);
   *  a Web-only account (no Telegram identity until the user links Telegram) answers 409 telegram_not_linked —
   *  the caller handles the HttpError itself */
  sendNewsNow: async () => (await write('POST', '/api/v1/news/send-now', ApiOutcome)).outcome as SendNowOutcome,
  /** «Сменить тариф»: the bot's rule — the current paid plan ends now, without compensation */
  cancelPlan: async () => { await explain(write('POST', '/api/v1/subscription/cancel', ApiOutcome, { confirm: true })); return fetchProfile(); },
  /** a pending payment of the session's account; the browser then goes to the provider's page */
  createPayment: async (o: { plan: string; months: number; method: 'card' | 'crypto' }) => {
    const period = PERIOD_OF[o.months];
    if (!period) throw new Error(srv('apiErr.generic'));
    const r = await explain(write('POST', '/api/v1/payments', ApiCheckout, { plan: o.plan.toLowerCase(), period, method: o.method, language: i18n.language }));
    return { id: r.id, checkoutUrl: r.checkout_url };
  },
  checkPayment,
  calendar: fetchCalendar,
  signals: fetchSignals,
  portfolioHistory: async (days: number) => (await requestJson(`/api/v1/portfolio/history?days=${days}`, { schema: ApiHistory })).days,
  wallStreet: fetchWallStreet,
  alerts: fetchAlerts,
  social: fetchSocial,
  socialX: fetchSocialX,
  socialYt: fetchSocialYt,
  ideas: fetchIdeas,
  notices: async () => (await requestJson('/api/v1/notifications', { schema: ApiNotices })).items.map(toNotice),
  markNoticesRead: (ids: string[] | 'all') => write('POST', '/api/v1/notifications/read', ApiMarked, ids === 'all' ? { all: true } : { ids }),
  setOrcaWatch: (p: { enabled?: boolean; networks?: string[]; threshold?: number }) => patchSettings({ orcawatch: {
    ...(p.enabled !== undefined ? { enabled: p.enabled } : {}), ...(p.networks ? { networks: p.networks } : {}),
    ...(p.threshold !== undefined ? { threshold_usd: p.threshold } : {}) } }).then(fetchAlerts),
  setPriceAlerts: (p: { enabled?: boolean }) => patchSettings({ price_alerts: { enabled: !!p.enabled } }).then(fetchAlerts),
  aiQuota: () => requestJson('/api/v1/ai/quota', { schema: ApiQuota }),
  aiAsk: (payload: { message: string; content: string; message_id: string; conversation_id?: string; context?: AiContextDescriptor }) =>
    write('POST', '/api/v1/ai/chat', ApiAiStart, payload),
  aiResult: (id: string) => write('POST', `/api/v1/ai/chat/${encodeURIComponent(id)}/result`, ApiAiResult, {}),
  aiConversations: async (search = '') => (await requestJson(`/api/v1/ai/conversations${search ? `?q=${encodeURIComponent(search)}` : ''}`, { schema: ApiAiConversationList })).items,
  aiConversation: (id: string) => requestJson(`/api/v1/ai/conversations/${encodeURIComponent(id)}`, { schema: ApiAiConversationDetail }),
  deleteAiConversation: (id: string) => write('DELETE', `/api/v1/ai/conversations/${encodeURIComponent(id)}`, Ok),
  webPreferences: () => requestJson('/api/v1/web/preferences', { schema: ApiWebPreferences }),
  completeWebTour: () => write('PATCH', '/api/v1/web/preferences', ApiWebPreferences, { tour_completed: true }),
  /** the profile photo: a small square image (data URL) the browser already cropped and resized */
  avatar: () => requestJson('/api/v1/profile/avatar', { schema: ApiAvatar }),
  setAvatar: (image: string) => write('PUT', '/api/v1/profile/avatar', ApiAvatar, { image }),
  removeAvatar: () => write('DELETE', '/api/v1/profile/avatar', ApiAvatar),
};
